import Text from 'components/Text/Text';
import React from 'react';
import { Input, InputNumber } from 'antd';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import { InputProps } from 'antd/lib/input/Input';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { TOptions } from 'i18next';
import style from './OutsideFormInput.module.scss';

type TDefaultInputProps = Omit<InputProps, 'onChange'>;

interface IOutsideFormInput extends TDefaultInputProps {
	name: string;
	label: string;
	value?: string;
	numberValue?: Nullable<number>;
	errorMessage?: string;
	blockId?: string;
	placeholder?: string;
	className?: string;
	containerClassName?: string;
	labelClassName?: string;
	testId?: string;
	type?: 'text' | 'password' | 'textArea' | 'number';
	withValidation?: boolean;
	translationOptions?: TOptions | string;
	min?: number;
	max?: number;
	precision?: number;
	onChange?: (value: string) => void;
	onChangeNumber?: (value: number | null) => void;
	getValueFromEvent?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const OutsideFormInput: React.FC<IOutsideFormInput> = ({
	name,
	label,
	value,
	numberValue,
	errorMessage = 'commonError',
	blockId,
	placeholder = 'inputPlaceholder',
	className,
	containerClassName,
	labelClassName,
	testId,
	type = 'text',
	withValidation = true,
	translationOptions,
	min,
	max,
	precision,
	disabled = false,
	onChange,
	onChangeNumber,
	...restProps
}) => {
	const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
		value,
		errorMessage,
		name,
		blockId,
	);
	const { t } = useTranslation('form');

	const containerClasses = classNames(style.container, containerClassName);
	const inputClasses = classNames(
		style.input,
		{ [style.inputError]: withValidation && isShowValidationError },
		className,
	);
	const textAreaClasses = classNames(
		style.textArea,
		{ [style.inputError]: withValidation && isShowValidationError },
		className,
	);
	const inputNumberClasses = classNames(style.inputNumber, className, { [style.disabled]: disabled });
	const labelClasses = classNames(style.label, labelClassName);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange?.(e.target.value);
	};

	const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		onChange?.(e.target.value);
	};

	const handleChangeInputNumber = (e: Nullable<number>) => {
		onChangeNumber?.(e);
	};

	const renderInputElement = () => {
		switch (type) {
			case 'text':
				return (
					<Input
						className={inputClasses}
						value={value}
						onChange={handleChange}
						allowClear={true}
						placeholder={t(placeholder)}
						{...restProps}
					/>
				);
			case 'password':
				return (
					<Input.Password
						className={inputClasses}
						value={value}
						onChange={handleChange}
						allowClear={true}
						placeholder={t(placeholder)}
						{...restProps}
					/>
				);
			case 'textArea':
				return (
					<Input.TextArea
						className={textAreaClasses}
						value={value}
						onChange={handleChangeTextArea}
						allowClear={true}
						autoSize={true}
						placeholder={t(placeholder)}
					/>
				);
			case 'number':
				return (
					<InputNumber
						className={inputNumberClasses}
						value={typeof numberValue === 'number' ? numberValue : undefined}
						onChange={handleChangeInputNumber}
						placeholder={t(placeholder)}
						min={min}
						max={max}
						disabled={disabled}
						precision={precision}
					/>
				);
			default:
				return (
					<Input
						className={inputClasses}
						value={value}
						onChange={handleChange}
						allowClear={true}
						placeholder={t(placeholder)}
						{...restProps}
					/>
				);
		}
	};

	return (
		<div data-test={testId} className={containerClasses}>
			<Text
				className={labelClasses}
				translationNameSpace="form"
				translationKey={label}
				translationOptions={translationOptions}
				size="xs"
				theme="neutralLight"
			/>
			{renderInputElement()}
			{withValidation && isShowValidationError && (
				<Text translationNameSpace="error" translationKey={validationErrorMessage} theme="error" size="xs" />
			)}
		</div>
	);
};

export default React.memo(OutsideFormInput);
